import { Icon } from "@iconify/react/dist/iconify.js";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import { usePermissions } from "../context/PermissionContext";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import SortIcons from "./ui/SortIcons";
import { format, parseISO } from "date-fns";
import ErrorAlert from "./ui/ErrorAlert";

const RolesList = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [modalType, setModalType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [roleId, setRoleId] = useState(null);
  const [modalError, setModalError] = useState("");
  const [modalSuccess, setModalSuccess] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [saveLoading, setSaveLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [expandedRows, setExpandedRows] = useState({});
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(parseISO(dateString), "dd MMM yyyy");
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Main fetch function - YEH FIXED HAI
  const fetchRoles = async (
    page = 1,
    perPageValue = 10,
    search = "",
    sortBy = null,
    sortOrder = "asc"
  ) => {
    setLoading(true);
    setErrorMessage("");
    
    try {
      const queryParams = new URLSearchParams({
        page: page,
        per_page: perPageValue,
      });
      
      if (search) queryParams.append("search", encodeURIComponent(search));
      if (sortBy) queryParams.append("sort_by", sortBy);
      if (sortOrder) queryParams.append("sort_order", sortOrder);

      const response = await apiCall(
        "get",
        `/get-all-roles?${queryParams.toString()}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        let roleList = [];
        let total = 0;
        let currentPageFromResponse = page;
        let lastPage = 1;

        const data = response.data;

        // Handle different response structures
        if (data.pagination) {
          // Response structure from your JSON
          roleList = data.data || [];
          total = data.pagination.total || 0;
          currentPageFromResponse = data.pagination.current_page || page;
          lastPage = data.pagination.last_page || 1;
        } else if (Array.isArray(data.data)) {
          roleList = data.data;
          total = data.total || roleList.length;
          currentPageFromResponse = data.current_page || page;
          lastPage = data.last_page || Math.ceil(total / perPageValue);
        } else if (data.data?.data) {
          roleList = data.data.data;
          total = data.data.total || 0;
          currentPageFromResponse = data.data.current_page || page;
          lastPage = data.data.last_page || 1;
        } else if (data.roles) {
          roleList = data.roles;
          total = data.total || roleList.length;
          currentPageFromResponse = data.current_page || page;
          lastPage = data.last_page || Math.ceil(total / perPageValue);
        }

        // Update state - YEH IMPORTANT HAI
        setRoles(roleList);
        setTotalEntries(total);
        setTotalPages(parseInt(lastPage, 10));
        setCurrentPage(parseInt(currentPageFromResponse, 10));
        
        handleApiResponse(response.data, "success", "GET");
      } else {
        handleCustomResponse("error", "Failed to load roles.");
        setErrorMessage(response?.data?.message || "Failed to load roles.");
      }
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "GET");
      setErrorMessage(error.response?.data?.message || "Error fetching roles.");
    } finally {
      setLoading(false);
    }
  };

  // Debounce function
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  // Debounced fetch - UPDATED
  const debouncedFetchRoles = useCallback(
    debounce((page, perPageValue, search, sortField, sortDirection) => {
      fetchRoles(page, perPageValue, search, sortField, sortDirection);
    }, 300),
    []
  );

  // Search handler - FIXED
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    debouncedFetchRoles(1, perPage, value, sortConfig.field, sortConfig.direction);
  };

  // Sort handler - FIXED
  const handleSort = (field) => {
    const newDirection =
      sortConfig.field === field && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ field, direction: newDirection });
    setCurrentPage(1); // Reset to first page on sort
    fetchRoles(1, perPage, searchQuery, field, newDirection);
  };

  // Initial load and page/perPage changes - FIXED
  useEffect(() => {
    fetchRoles(currentPage, perPage, searchQuery, sortConfig.field, sortConfig.direction);
  }, [currentPage, perPage]);

  // Modal handlers
  const handleOpenModal = async (type, role = null) => {
    setModalType(type);
    setShowModal(true);
    setModalError("");
    setModalSuccess("");
    setRoleId(null);
    setSelectedRole(null);

    if (type === "delete" && role) {
      if (!role.role_id || isNaN(role.role_id)) {
        setModalError("Invalid or missing Role ID for this role.");
        return;
      }
      setSelectedRole(role);
      setRoleId(role.role_id);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalType("");
    setRoleId(null);
    setModalError("");
    setModalSuccess("");
    setSelectedRole(null);
  };

  const handleDeleteRole = async () => {
    if (!roleId || isNaN(roleId)) {
      setModalError("Invalid or missing Role ID.");
      handleCustomResponse("error", "Invalid or missing Role ID.");
      return;
    }

    try {
      setSaveLoading(true);
      const response = await apiCall(
        "delete",
        `/delete-role/${roleId}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        handleApiResponse(response.data, "success", "DELETE");
        setModalError("");
        await fetchRoles(currentPage, perPage, searchQuery, sortConfig.field, sortConfig.direction);
        handleCloseModal();
      } else {
        handleCustomResponse("error", "Failed to delete role.");
        setModalError(response?.data?.message || "Failed to delete role.");
      }
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "DELETE");
      setModalError(error.response?.data?.message || "Error deleting role.");
      setModalSuccess("");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSubmit = () => {
    if (modalType === "delete") {
      handleDeleteRole();
    }
  };

  // Page change handler - FIXED
  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Per page change handler - FIXED
  const handlePerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
  };

  // Truncate text
  const truncateText = (text, maxLength = 50) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Toggle expand
  const toggleExpand = (roleId, field) => {
    setExpandedRows((prev) => ({
      ...prev,
      [`${roleId}_${field}`]: !prev[`${roleId}_${field}`],
    }));
  };

  if (errorMessage && roles.length === 0) {
    return <ErrorAlert />;
  }

  return (
    <div className="p-4 p-md-3 p-sm-2">
      <style>
        {`
          .table-responsive .table td, .table-responsive .table th {
            max-width: 200px;
            vertical-align: middle;
          }
          .show-more-btn {
            color: #E63C23;
            cursor: pointer;
            font-size: 12px;
            margin-left: 5px;
            background: none;
            border: none;
            padding: 0;
          }
          .show-more-btn:hover {
            text-decoration: underline;
          }
          @media (max-width: 576px) {
            .table-responsive .table td, .table-responsive .table th {
              max-width: 100px;
            }
            .show-more-btn {
              font-size: 10px;
            }
          }
        `}
      </style>
      <div className="p-4 p-md-3 p-sm-2">
        <div className="card shadow-sm">
          <div className="card p-3 p-sm-2 p-md-3">
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              <div
                className="icon-field flex-grow-1"
                style={{ maxWidth: "400px" }}
              >
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search by role name..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                <span className="icon">
                  <Icon icon="ion:search-outline" />
                </span>
              </div>
              <PerPageDropdown
                perPage={perPage}
                handlePerPageChange={handlePerPageChange}
              />
              <div className="ms-auto">
                {hasPermission("insert_roles") && (
                  <button
                    className="btn btn-primary-600 btn-sm d-flex align-items-center"
                    onClick={() => navigate("/add-role")}
                  >
                    <Icon icon="ri:add-line" className="me-1" /> Add Role
                  </button>
                )}
              </div>
            </div>

            {errorMessage && (
              <div
                className="alert alert-danger alert-dismissible fade show mb-3"
                role="alert"
              >
                {errorMessage}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setErrorMessage("")}
                ></button>
              </div>
            )}

            {roles.length === 0 && !loading ? (
              <TableDataNoAvailable message="No roles found." />
            ) : (
              <div className="position-relative">
                <div
                  className="table-responsive"
                  style={{
                    opacity: loading ? 0.4 : 1,
                    pointerEvents: loading ? "none" : "auto",
                  }}
                >
                  <table className="table bordered-table mb-0 table-hover">
                    <thead>
                      <tr
                        style={{ backgroundColor: "#475EAB", color: "white" }}
                      >
                        <th scope="col" className="p-2">
                          <span>S.No</span>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("role_name")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Role Name</span>
                            <SortIcons
                              field="role_name"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>

                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("description")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Description</span>
                            <SortIcons
                              field="description"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>

                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("created_by")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Created By</span>
                            <SortIcons
                              field="created_by"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("created_datetime")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Created Date</span>
                            <SortIcons
                              field="created_datetime"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("is_active")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex">
                            <span>Status</span>
                            <SortIcons
                              field="is_active"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th scope="col" className="p-2">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {roles.map((role, index) => (
                        <tr key={role.role_id || index}>
                          <td className="p-2">
                            {(currentPage - 1) * perPage + index + 1}
                          </td>
                          <td className="p-2">
                            <span className="">
                              {expandedRows[`${role.role_id}_role_name`]
                                ? role.role_name || "-"
                                : truncateText(role.role_name || "-")}
                              {role.role_name && role.role_name.length > 50 && (
                                <button
                                  className="show-more-btn"
                                  onClick={() =>
                                    toggleExpand(role.role_id, "role_name")
                                  }
                                >
                                  {expandedRows[`${role.role_id}_role_name`]
                                    ? "Show Less"
                                    : "Show More"}
                                </button>
                              )}
                            </span>
                          </td>

                          <td className="p-2">
                            {expandedRows[`${role.role_id}_description`]
                              ? role.description || "-"
                              : truncateText(role.description || "-")}
                            {role.description &&
                              role.description.length > 50 && (
                                <button
                                  className="show-more-btn"
                                  onClick={() =>
                                    toggleExpand(role.role_id, "description")
                                  }
                                >
                                  {expandedRows[`${role.role_id}_description`]
                                    ? "Show Less"
                                    : "Show More"}
                                </button>
                              )}
                          </td>
                          <td className="p-2">{role.created_by || "-"}</td>
                          <td className="p-2">
                            {formatDate(role.created_datetime)}
                          </td>
                          <td className="p-2">
                            <span
                              className={`px-3 py-1 radius-4 fw-medium text-sm ${
                                role.is_active
                                  ? "bg-success-focus text-success-600 border border-success-main"
                                  : "bg-danger-focus text-danger-600 border border-danger-main"
                              }`}
                            >
                              {role.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="p-2">
                            {hasPermission("update_roles") && (
                              <button
                                className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                style={{
                                  position: "relative",
                                  transition: "all 0.3s ease",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "scale(1.1)";
                                  const tooltip = document.createElement("div");
                                  tooltip.textContent = "Edit";
                                  tooltip.style.cssText = `
                                position: absolute;
                                top: -35px;
                                left: 50%;
                                transform: translateX(-50%);
                                background-color: #333;
                                color: white;
                                padding: 5px 8px;
                                border-radius: 4px;
                                font-size: 12px;
                                white-space: nowrap;
                                z-index: 1000;
                                pointer-events: none;
                              `;
                                  tooltip.id = "edit-tooltip";
                                  e.currentTarget.appendChild(tooltip);
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "scale(1)";
                                  const tooltip =
                                    document.getElementById("edit-tooltip");
                                  if (tooltip) tooltip.remove();
                                }}
                                onClick={() =>
                                  navigate(`/edit-role/${role.role_id}`)
                                }
                              >
                                <Icon icon="lucide:edit" />
                              </button>
                            )}
                            {hasPermission("insert_user") && (
                              <button
                                className="w-32-px h-32-px me-8 bg-warning-focus text-warning-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                style={{
                                  position: "relative",
                                  transition: "all 0.3s ease",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "scale(1.1)";
                                  const tooltip = document.createElement("div");
                                  tooltip.textContent = "Add User";
                                  tooltip.style.cssText = `
                                position: absolute;
                                top: -35px;
                                left: 50%;
                                transform: translateX(-50%);
                                background-color: #333;
                                color: white;
                                padding: 5px 8px;
                                border-radius: 4px;
                                font-size: 12px;
                                white-space: nowrap;
                                z-index: 1000;
                                pointer-events: none;
                              `;
                                  tooltip.id = "add-user-tooltip";
                                  e.currentTarget.appendChild(tooltip);
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "scale(1)";
                                  const tooltip =
                                    document.getElementById("add-user-tooltip");
                                  if (tooltip) tooltip.remove();
                                }}
                                onClick={() =>
                                  navigate(`/add-user?roleId=${role.role_id}`)
                                }
                              >
                                <Icon icon="ri:user-add-line" />
                              </button>
                            )}
                            {hasPermission("assign_user_permission") && (
                              <button
                                className="w-32-px h-32-px me-8 bg-info-focus text-info-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                style={{
                                  position: "relative",
                                  transition: "all 0.3s ease",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "scale(1.1)";
                                  const tooltip = document.createElement("div");
                                  tooltip.textContent = "Assign Permissions";
                                  tooltip.style.cssText = `
                                position: absolute;
                                top: -35px;
                                left: 50%;
                                transform: translateX(-50%);
                                background-color: #333;
                                color: white;
                                padding: 5px 8px;
                                border-radius: 4px;
                                font-size: 12px;
                                white-space: nowrap;
                                z-index: 1000;
                                pointer-events: none;
                              `;
                                  tooltip.id = "assign-permissions-tooltip";
                                  e.currentTarget.appendChild(tooltip);
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "scale(1)";
                                  const tooltip = document.getElementById(
                                    "assign-permissions-tooltip"
                                  );
                                  if (tooltip) tooltip.remove();
                                }}
                                onClick={() =>
                                  navigate(
                                    `/assign-permissions?roleId=${role.role_id}`
                                  )
                                }
                              >
                                <Icon icon="mdi:shield-key-outline" />
                              </button>
                            )}
                            {hasPermission("delete_roles") && (
                              <button
                                className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                style={{
                                  position: "relative",
                                  transition: "all 0.3s ease",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "scale(1.1)";
                                  const tooltip = document.createElement("div");
                                  tooltip.textContent = "Delete";
                                  tooltip.style.cssText = `
                                position: absolute;
                                top: -35px;
                                left: 50%;
                                transform: translateX(-50%);
                                background-color: #333;
                                color: white;
                                padding: 5px 8px;
                                border-radius: 4px;
                                font-size: 12px;
                                white-space: nowrap;
                                z-index: 1000;
                                pointer-events: none;
                              `;
                                  tooltip.id = "delete-tooltip";
                                  e.currentTarget.appendChild(tooltip);
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "scale(1)";
                                  const tooltip =
                                    document.getElementById("delete-tooltip");
                                  if (tooltip) tooltip.remove();
                                }}
                                onClick={() => handleOpenModal("delete", role)}
                              >
                                <Icon icon="mingcute:delete-2-line" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {loading && (
                  <div
                    className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                    style={{
                      zIndex: 10,
                      borderRadius: "10px",
                      padding: "20px",
                    }}
                  >
                    <LoadingComponent />
                  </div>
                )}
                <PaginationControls
                  pagination={{
                    currentPage,
                    perPage,
                    total: totalEntries,
                  }}
                  handlePageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${showModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Delete Role</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
              ></button>
            </div>
            <div className="modal-body">
              {modalError && (
                <div className="alert alert-danger">{modalError}</div>
              )}
              {modalSuccess && (
                <div className="alert alert-success">{modalSuccess}</div>
              )}
              <p>
                Are you sure you want to delete{" "}
                <strong>{selectedRole?.role_name || "this role"}</strong>?
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleSubmit}
                disabled={saveLoading}
              >
                {saveLoading ? "Please wait..." : "Delete Role"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolesList;